import React, { useState } from 'react';
import classNames from 'classnames';
import { useAuthContext } from '../../../context/AuthContext';
import { ChatMessagePayload } from '../../../hooks/useChatSocket';
import { ChatConversation } from '../../../context/ChatContext';

interface ProposalCardProps {
    message: ChatMessagePayload;
    activeConversation: ChatConversation;
    onAccept: (selectedDate: string) => Promise<void>;
    onRequestOther: () => void;
    isActive: boolean;
    inactiveReason?: string;
}

export const ProposalCard: React.FC<ProposalCardProps> = ({
    message,
    activeConversation,
    onAccept,
    onRequestOther,
    isActive,
    inactiveReason
}) => {
    const { user } = useAuthContext();
    const proposals = message.metadata?.proposedDates || [];

    // senderId is 'me' or 'other' (set by loadMessagesForConversation)
    const isMine = message.senderId === 'me';

    // The professional always proposes.
    // isMine => current user is the professional => proposer name = user.first_name
    // !isMine => current user is the patient => proposer = otherUser (professional)
    const proposerName = isMine
        ? (user?.first_name || 'Tú')
        : activeConversation.otherUser.firstName;

    // The patient is always the receiver.
    const receiverName = isMine
        ? activeConversation.otherUser.firstName
        : (user?.first_name || 'Tú');

    // Only the receiver of the proposal can select and accept (i.e., !isMine)
    const interactive = isActive && !isMine;

    const [selectedDate, setSelectedDate] = useState<string>(proposals[0] || '');

    const plural = proposals.length > 1;

    const formatDate = (val: string) => {
        if (!val) return { dateString: 'Fecha pendiente', timeString: '' };
        try {
            const normalizedVal = val.includes('T') ? val : val.replace(' ', 'T') + 'Z';
            const date = new Date(normalizedVal);
            const formatted = new Intl.DateTimeFormat('es-UY', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                timeZone: 'UTC'
            }).format(date);
            const time = new Intl.DateTimeFormat('es-UY', {
                hour: '2-digit',
                minute: '2-digit',
                timeZone: 'UTC'
            }).format(date);
            return {
                dateString: formatted.charAt(0).toUpperCase() + formatted.slice(1),
                timeString: time,
            };
        } catch {
            return { dateString: val, timeString: '' };
        }
    };

    if (!isActive) {
        return (
            <article className="system-message proposal-history">
                <div className="system-title">
                    <span className="status-icon history">↺</span>
                    <span className="system-title-copy">
                        <strong>Propuesta anterior</strong>
                        <span>{inactiveReason || 'Se ha respondido a esta propuesta o se ha vencido.'}</span>
                    </span>
                </div>
                <div className="proposal-list">
                    {proposals.map((prop: string, idx: number) => {
                        const { dateString, timeString } = formatDate(prop);
                        return (
                            <button key={idx} className="proposal-choice" type="button" disabled>
                                <span className="proposal-radio"></span>
                                <span className="proposal-copy">
                                    <strong>{dateString} · {timeString}</strong>
                                    <span>Presencial / Online</span>
                                </span>
                                <span className="proposal-price">$U {message.metadata?.consultPrice || '3.500'}</span>
                            </button>
                        );
                    })}
                </div>
                <p className="system-foot">
                    La propuesta queda registrada en la conversación y ya no admite selección.
                </p>
            </article>
        );
    }

    return (
        <article className="system-message">
            <div className="system-title">
                <span className="status-icon">✓</span>
                <span className="system-title-copy">
                    <strong>
                        {proposerName} propuso {plural ? 'alternativas' : 'un horario'}
                    </strong>
                    <span>La propuesta activó el plazo de 12 horas.</span>
                </span>
            </div>

            <div className="proposal-list">
                {proposals.map((prop: string, idx: number) => {
                    const { dateString, timeString } = formatDate(prop);
                    return (
                        <button
                            key={idx}
                            className={classNames('proposal-choice', {
                                'is-selected': interactive && selectedDate === prop,
                            })}
                            type="button"
                            disabled={!interactive}
                            onClick={() => {
                                if (interactive) setSelectedDate(prop);
                            }}
                        >
                            <span className="proposal-radio"></span>
                            <span className="proposal-copy">
                                <strong>{dateString} · {timeString}</strong>
                                <span>Presencial / Online</span>
                            </span>
                            <span className="proposal-price">$U {message.metadata?.consultPrice || '3.500'}</span>
                        </button>
                    );
                })}
            </div>

            <div className="deadline">
                <span>Elegir e iniciar el pago</span>
                <strong>12 h 00 min</strong>
            </div>

            {interactive && (
                <>
                    <div className="chat-actions">
                        <button
                            className="chat-button primary"
                            type="button"
                            onClick={() => onAccept(selectedDate)}
                        >
                            Aceptar y pagar
                        </button>
                        <button
                            className="chat-button secondary"
                            type="button"
                            onClick={onRequestOther}
                        >
                            Otro horario
                        </button>
                    </div>
                    <a className="system-link" href="#ver-estado">Ver estado al vencer las 12 h</a>
                </>
            )}

            {!interactive && isMine && (
                <>
                    <p className="system-foot">
                        {receiverName} recibió la propuesta y puede elegir desde esta misma conversación.
                    </p>
                    <a className="system-link" href="#ver-estado">Ver estado al vencer las 12 h</a>
                </>
            )}
        </article>
    );
};
